import { RadioButton } from "primereact/radiobutton";
import React, { useState } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import useApp from "hooks/useApp";
import useApi from "hooks/useApi";
import Loader from "pages/loader";
import { useTranslation } from 'react-i18next';
const languages = [
  { name: "English", key: "en" },
  { name: "Malay", key: "ms" },
  { name: "Thai", key: "th" },
];

const primeThemes = [
  {
    src: "lara-light-indigo.png",
    alt: "Lara Light Indigo",
    key: "lara-light-indigo",
  },
  {
    src: "lara-light-blue.png",
    alt: "Lara Light Blue",
    key: "lara-light-blue",
  },
  {
    src: "lara-light-purple.png",
    alt: "Lara Light Purple",
    key: "lara-light-purple",
  },
  {
    src: "lara-light-teal.png",
    alt: "Lara Light Teal",
    key: "lara-light-teal",
  },
  {
    src: "lara-dark-indigo.png",
    alt: "Lara Dark Indigo",
    key: "lara-dark-indigo",
  },
  { src: "lara-dark-blue.png", alt: "Lara Dark Blue", key: "lara-dark-blue" },
  {
    src: "lara-dark-purple.png",
    alt: "Lara Dark Purple",
    key: "lara-dark-purple",
  },
  { src: "lara-dark-teal.png", alt: "Lara Dark Teal", key: "lara-dark-teal" },
  { src: "soho-light.png", alt: "Soho Light", key: "soho-light" },
  { src: "soho-dark.png", alt: "Soho Dark", key: "soho-dark" },
  { src: "viva-light.svg", alt: "Viva Light", key: "viva-light" },
  { src: "viva-dark.svg", alt: "Viva Dark", key: "viva-dark" },
];

const materialThemes = [
  { src: "md-light-indigo.svg", alt: "Material Light Blue", key: "md-light-indigo" },
  { src: "md-light-deeppurple.svg", alt: "Material Light Deep Purple", key: "md-light-deeppurple" },
  { src: "md-dark-indigo.svg", alt: "Material Dark Indigo", key: "md-dark-indigo" },
  { src: "md-dark-deeppurple.svg", alt: "Material Dark Deep Purple", key: "md-dark-deeppurple" }
];

const bootstrapThemes = [
  {
    src: "bootstrap4-light-blue.svg",
    alt: "Bootstrap Light Blue",
    key: "bootstrap4-light-blue",
  },
  {
    src: "bootstrap4-light-purple.svg",
    alt: "Bootstrap Light Purple",
    key: "bootstrap4-light-purple",
  },
  {
    src: "bootstrap4-dark-blue.svg",
    alt: "Bootstrap Dark Blue",
    key: "bootstrap4-dark-blue",
  },
  {
    src: "bootstrap4-dark-purple.svg",
    alt: "Bootstrap Dark Purple",
    key: "bootstrap4-dark-purple",
  },
];

const materialCompactThemes = [
  { src: "md-light-indigo.svg", alt: "Material Compact Light Blue", key: "mdc-light-indigo" },
  { src: "md-light-deeppurple.svg", alt: "Material Compact Light Deep Purple", key: "mdc-light-deeppurple" },
  { src: "md-dark-indigo.svg", alt: "Material Compact Dark Indigo", key: "mdc-dark-indigo" },
  { src: "md-dark-deeppurple.svg", alt: "Material Compact Dark Deep Purple", key: "mdc-dark-deeppurple" }
]

const Settings = () => {
  const app = useApp();
  const api = useApi();
  const{t}=useTranslation();
  const [loading, setLoading] = useState(false);
  const _changeTheme = (newTheme) => {
    setLoading(true);
    const payload = {
      setting_name: "theme",
      config: newTheme,
    };
    api
      .put(`dashboard/edit_dashboard_setting/${app?.theme?.id}`, payload)
      .then(async (res) => {
        app.setTheme({
          ...payload,
          id: app?.theme?.id,
        });
        await app.getAllSettings();
      }).finally(() => setLoading(false))
  };
  const changeLanguage = (lng) => {
    setLoading(true);
    const payload = {
      setting_name: "language",
      config: lng,
    };
    api
      .put(`dashboard/edit_dashboard_setting/${app?.language?.id}`, payload)
      .then(async (res) => {
        await app.getAllSettings();
      }).finally(() => setLoading(false))
  };
  const getSelectedIcon = () => (
    <i
      className="pi pi-check-circle"
      style={{ fontSize: "1.5rem", color: "green", fontWeight: "bold" }}
    />
  );
  const renderThemesContent = (themesArray, groupName) => (
    <div className="grid">
      {themesArray.map((primetheme, index) => (
        <div className="col-3 flex gap-3 align-items-center" key={index}>
          <button
            className="p-link flex align-items-center"
            onClick={() => _changeTheme(primetheme.key)}
            title={primetheme.alt}
            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
          >
            <img
              src={`/layout/images/themes/${primetheme.src}`}
              className="w-2rem h-2rem"
              alt={primetheme.alt}
            />
            <span className="ml-2">
              {primetheme.alt}
              {primetheme.key === "mdc-light-indigo" && (
                <span style={{ color: 'green' }}> (Default)</span>
              )}
            </span>
          </button>
          {app?.theme?.config === primetheme.key && getSelectedIcon()}
        </div>
      ))}
    </div>
  );
  const getThemes = () => (
    <React.Fragment>
      <h5>PrimeOne Design</h5>
      {renderThemesContent(primeThemes)}
      <h5>Bootstrap</h5>
      {renderThemesContent(bootstrapThemes)}
      <h5>Material Design</h5>
      {renderThemesContent(materialThemes)}
      <h5>Material Design Compact</h5>
      {renderThemesContent(materialCompactThemes)}
    </React.Fragment>
  );
  return (
    <div className="mx-auto card">
      {loading && <Loader />}
      <i
        className="pi pi-arrow-circle-left cursor-pointer"
        onClick={() => app.navigate(-1)}
        style={{ fontSize: "2rem" }}
      />
      <TabView>
        <TabPanel header={t("THEMES")} leftIcon="pi pi-microsoft mr-2">
          {getThemes()}
        </TabPanel>
        <TabPanel header={t("LANGUAGE")} leftIcon="pi pi-language mr-2">
          <div className="flex flex-column gap-3">
            {languages.map((language) => {
              return (
                <div key={language.key} className="flex align-items-center">
                  <RadioButton
                    inputId={language.key}
                    name="language"
                    value={language}
                    onChange={(e) => {
                      changeLanguage(e?.value?.key);
                    }}
                    checked={app?.language?.config === language.key}
                  />
                  <label htmlFor={language.key} className="ml-2">
                    {language.name}
                    {language.key === "en" && (
                      <span className="ml-2" style={{ color: "green" }}>(Default)</span>
                    )}
                  </label>
                </div>  
              );
            })}
          </div>
        </TabPanel>
      </TabView>
    </div>
  );
};

export default Settings;
